import colors from 'theme/colors';

/**
 * Tokens e leitura de dinheiro da aba Pedidos do evento.
 *
 * A aba mostra `/financial/transactions` recortado pelo evento — a mesma fonte
 * da aba Transações do Financeiro. Os rótulos e as cores de status são
 * deliberadamente os mesmos das outras telas: o produtor não deveria precisar
 * reaprender o vocabulário ao trocar de tela.
 *
 * Acessibilidade: todo chip é fundo claro + texto escuro, acima de 4,5:1 sobre
 * o próprio fundo. Status nunca é comunicado só por cor — o rótulo vem junto.
 */

export const statusChip = {
    approved: { label: 'Aprovado', bg: '#DCFCE7', text: '#166534', border: '#B7E5C9' },
    pending: { label: 'Aguardando pagamento', bg: '#FEF3C7', text: '#92400E', border: '#F3DFAE' },
    canceled: { label: 'Expirado', bg: '#FEE2E2', text: '#B3261E', border: '#F5C2C7' },
    refused: { label: 'Recusado', bg: '#FEE2E2', text: '#B3261E', border: '#F5C2C7' },
    refunded: { label: 'Estornado', bg: '#F3F4F6', text: '#374151', border: '#E2E2E6' },
    chargeback: { label: 'Chargeback', bg: '#FEE2E2', text: '#991B1B', border: '#F5C2C7' },
    in_dispute: { label: 'Em disputa', bg: '#FEF3C7', text: '#92400E', border: '#F3DFAE' },
};

export function chipOf(status) {
    return statusChip[status] || statusChip.pending;
}

/** Só pedido aprovado vira saldo; o resto é dinheiro que não chegou. */
export const CREDITS_BALANCE = 'approved';

/**
 * Pedido expirado, recusado ou aguardando pagamento chega com `gross_value` e
 * `tax_value` preenchidos — são os valores que *teriam* sido cobrados. Exibi-los
 * com o mesmo peso de uma venda real faz a lista somar dinheiro que nunca
 * existiu na cabeça de quem lê. Aqui eles continuam visíveis (o produtor precisa
 * saber quanto deixou na mesa), mas apagados e no tempo condicional.
 */
const MONEY_MOVED = ['approved', 'refunded', 'chargeback', 'in_dispute'];

export function moneyMoved(status) {
    return MONEY_MOVED.includes(status);
}

export const STATUS_OPTIONS = [
    { value: '', label: 'Todos os status' },
    { value: 'approved', label: 'Aprovado' },
    { value: 'pending', label: 'Aguardando pagamento' },
    { value: 'canceled', label: 'Expirado' },
    { value: 'refunded', label: 'Estornado' },
    { value: 'refused', label: 'Recusado' },
    { value: 'chargeback', label: 'Chargeback' },
];

export const PAYMENT_OPTIONS = [
    { value: '', label: 'Todas as formas' },
    { value: 'credit_card', label: 'Cartão de crédito' },
    { value: 'pix', label: 'PIX' },
    { value: 'free', label: 'Grátis' },
];

export const PERIOD_OPTIONS = [
    { value: 'all', label: 'Tudo' },
    { value: 'today', label: 'Hoje' },
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
];

export const CUSTOM_PERIOD = 'custom';

const round = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Abre um pedido na cadeia que o produtor precisa enxergar:
 *
 *     itens − desconto + taxa de serviço + juros = o que o comprador pagou
 *     o que o comprador pagou − retido pela plataforma = o seu líquido
 *
 * A taxa de serviço é lida como **resíduo** do que o comprador pagou, e não
 * como `tax_value − interest`. Quando o produtor absorve a taxa, ela sai do
 * líquido dele sem nunca entrar no valor cobrado — a subtração inventaria uma
 * linha que não existe no extrato do comprador. Como resíduo, ela some sozinha
 * nesse caso, e `absorbedFee` explica o buraco entre pago e líquido.
 */
export function readMoneyChain(transaction) {
    const subtotal = round(transaction?.subtotal);
    const discount = round(transaction?.discount);
    const interest = round(transaction?.interest);
    const paid = round(transaction?.gross_value);
    const kept = round(transaction?.tax_value);
    const net = round(transaction?.net_value);

    const items = round(subtotal - discount);
    const serviceFee = round(paid - items - interest);

    return {
        subtotal,
        discount,
        interest,
        paid,
        kept,
        net,
        items,
        serviceFee: serviceFee > 0 ? serviceFee : 0,
        // Retenção que não foi repassada ao comprador: o produtor pagou por ela.
        absorbedFee: serviceFee <= 0 && kept > 0,
        creditsBalance: transaction?.status === CREDITS_BALANCE,
        moved: moneyMoved(transaction?.status),
    };
}

export const formatInt = (value) => Number(value || 0).toLocaleString('pt-BR');

export function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString('pt-BR');
}

export function formatTime(value) {
    if (!value) return '';
    return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export const surface = {
    border: colors.neutralDark,
    bg: colors.neutralLight,
    muted: '#FAFAFB',
};
